import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookMarked, Check, Plus, Sparkles, Trash2, RefreshCw } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  addRequirementFromCatalog,
  createRequirement,
  deleteRequirement,
  generateUpcomingTasks,
  listCatalog,
  listRequirements,
  updateRequirement,
  type CatalogRow,
  type ComplianceAuthority,
  type ComplianceCategory,
  type RequirementRow,
} from "@/lib/api/compliance";
import { listFunds } from "@/lib/api/funds";
import {
  AUTHORITY_LABEL,
  CATEGORY_LABEL,
  FREQUENCY_LABEL,
  SOURCE_LABEL,
} from "@/lib/compliance-labels";
import type { ComplianceFrequency } from "@argus/engine";

const TODAY = new Date().toISOString().slice(0, 10);

function RequirementsInner() {
  const tenant = useTenant();
  const qc = useQueryClient();
  const { canWrite } = useRole(tenant.id);
  const [tab, setTab] = useState<"active" | "catalog" | "custom">("active");

  const { data: requirements, isLoading } = useQuery({
    queryKey: ["compliance-requirements", tenant.id],
    queryFn: () => listRequirements(tenant.id),
  });
  const { data: catalog } = useQuery({ queryKey: ["regulatory-catalog"], queryFn: listCatalog });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const addedKeys = useMemo(
    () => new Set((requirements ?? []).map((r) => r.catalog_key).filter(Boolean) as string[]),
    [requirements],
  );

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["compliance-requirements", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["compliance-calendar", tenant.id] });
  }

  const addCatalog = useMutation({
    mutationFn: async (c: CatalogRow) => {
      const req = await addRequirementFromCatalog(tenant.id, c);
      await generateUpcomingTasks(req, TODAY, 4);
    },
    onSuccess: invalidate,
  });

  const generate = useMutation({
    mutationFn: (r: RequirementRow) => generateUpcomingTasks(r, TODAY, 4),
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: (r: RequirementRow) => updateRequirement(r.id, { active: !r.active }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteRequirement(id),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        title="Compliance requirements"
        subtitle="Define what this firm must do and when. Add standard obligations from the regulatory catalog or author your own — each generates dated tasks on the calendar."
        actions={
          <Link to="/t/$slug/calendar" params={{ slug: tenant.slug }}>
            <Button variant="secondary">View calendar</Button>
          </Link>
        }
      />

      <div className="flex gap-1 border-b border-neutral-800">
        {(
          [
            ["active", `Active (${requirements?.length ?? 0})`],
            ["catalog", "Regulatory catalog"],
            ["custom", "Custom"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
              tab === k
                ? "border-brand-500 text-neutral-100"
                : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {(addCatalog.error ?? generate.error ?? remove.error) && (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {(addCatalog.error ?? generate.error ?? remove.error)?.message}
        </div>
      )}

      {tab === "active" &&
        (isLoading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : !requirements || requirements.length === 0 ? (
          <EmptyState
            icon={<BookMarked className="size-7" />}
            title="No requirements yet"
            description="Start from the regulatory catalog — Form ADV, Form PF, code of ethics, custody rule, and more — then refine."
            action={
              canWrite ? (
                <Button variant="primary" onClick={() => setTab("catalog")}>
                  <Plus className="size-4" /> Browse catalog
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-2">
            {requirements.map((r) => (
              <Card key={r.id} className={`px-4 py-3 ${r.active ? "" : "opacity-60"}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-neutral-100">{r.title}</span>
                      <Badge tone="neutral">{AUTHORITY_LABEL[r.authority]}</Badge>
                      <Badge tone="info">{SOURCE_LABEL[r.source]}</Badge>
                      {!r.active && <Badge tone="neutral">Inactive</Badge>}
                    </div>
                    <div className="mt-1 text-[12px] text-neutral-500">
                      {CATEGORY_LABEL[r.category]} · {FREQUENCY_LABEL[r.frequency]} ·{" "}
                      {r.scope === "fund"
                        ? (funds?.find((f) => f.id === r.fund_id)?.name ?? "Fund")
                        : r.scope === "lp"
                          ? "LP-specific"
                          : "Firm-wide"}
                      {r.citation ? ` · ${r.citation}` : ""}
                      {r.checklist_template.length > 0
                        ? ` · ${r.checklist_template.length}-item checklist`
                        : ""}
                    </div>
                    {r.description && (
                      <p className="mt-1.5 line-clamp-2 text-[12px] text-neutral-400">
                        {r.description}
                      </p>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Generate upcoming tasks"
                        disabled={generate.isPending}
                        onClick={() => generate.mutate(r)}
                      >
                        <RefreshCw className="size-3.5" /> Generate tasks
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive.mutate(r)}>
                        {r.active ? "Pause" : "Resume"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Delete"
                        onClick={() => {
                          if (confirm(`Delete "${r.title}" and its tasks?`)) remove.mutate(r.id);
                        }}
                      >
                        <Trash2 className="size-3.5 text-red-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ))}

      {tab === "catalog" && (
        <CatalogPicker
          catalog={catalog ?? []}
          addedKeys={addedKeys}
          canWrite={canWrite}
          pending={addCatalog.isPending ? addCatalog.variables?.key : undefined}
          onAdd={(c) => addCatalog.mutate(c)}
        />
      )}

      {tab === "custom" && (
        <CustomRequirementForm
          tenantId={tenant.id}
          canWrite={canWrite}
          onCreated={() => {
            invalidate();
            setTab("active");
          }}
        />
      )}
    </>
  );
}

function CatalogPicker({
  catalog,
  addedKeys,
  canWrite,
  pending,
  onAdd,
}: {
  catalog: CatalogRow[];
  addedKeys: Set<string>;
  canWrite: boolean;
  pending: string | undefined;
  onAdd: (c: CatalogRow) => void;
}) {
  const byAuthority = useMemo(() => {
    const m = new Map<string, CatalogRow[]>();
    for (const c of catalog) {
      const list = m.get(c.authority) ?? [];
      list.push(c);
      m.set(c.authority, list);
    }
    return [...m.entries()];
  }, [catalog]);

  if (catalog.length === 0) return <p className="text-sm text-neutral-500">Loading catalog…</p>;

  return (
    <div className="space-y-5">
      <p className="flex items-center gap-2 text-xs text-neutral-500">
        <Sparkles className="text-brand-400 size-3.5" />
        Standard US private-fund adviser obligations. Adding one creates a requirement and schedules
        its next four tasks (with a starter checklist).
      </p>
      {byAuthority.map(([authority, items]) => (
        <div key={authority} className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
            {AUTHORITY_LABEL[authority as ComplianceAuthority]}
          </h3>
          <div className="grid gap-2">
            {items.map((c) => {
              const added = addedKeys.has(c.key);
              return (
                <Card key={c.key} className="flex items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-neutral-100">{c.title}</span>
                      <Badge tone="neutral">{FREQUENCY_LABEL[c.default_frequency]}</Badge>
                      {c.citation && (
                        <span className="text-[11px] text-neutral-600">{c.citation}</span>
                      )}
                    </div>
                    {c.description && (
                      <p className="mt-1 text-[12px] text-neutral-400">{c.description}</p>
                    )}
                    {c.typical_due && (
                      <p className="mt-1 text-[11px] text-neutral-600">Timing: {c.typical_due}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {added ? (
                      <Badge tone="clear">
                        <Check className="size-3" /> Added
                      </Badge>
                    ) : canWrite ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={pending === c.key}
                        onClick={() => onAdd(c)}
                      >
                        {pending === c.key ? (
                          <Spinner className="size-3.5" />
                        ) : (
                          <Plus className="size-3.5" />
                        )}
                        Add
                      </Button>
                    ) : null}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const CATEGORIES = Object.keys(CATEGORY_LABEL) as ComplianceCategory[];
const AUTHORITIES = Object.keys(AUTHORITY_LABEL) as ComplianceAuthority[];
const FREQUENCIES = Object.keys(FREQUENCY_LABEL) as ComplianceFrequency[];

function CustomRequirementForm({
  tenantId,
  canWrite,
  onCreated,
}: {
  tenantId: string;
  canWrite: boolean;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ComplianceCategory>("internal_policy");
  const [authority, setAuthority] = useState<ComplianceAuthority>("internal");
  const [frequency, setFrequency] = useState<ComplianceFrequency>("annual");
  const [lead, setLead] = useState(30);

  const create = useMutation({
    mutationFn: async () => {
      const req = await createRequirement({
        tenant_id: tenantId,
        title: title.trim(),
        description: description.trim() || null,
        category,
        authority,
        source: "manual",
        scope: "firm",
        frequency,
        default_lead_time_days: lead,
      });
      await generateUpcomingTasks(req, TODAY, 4);
    },
    onSuccess: onCreated,
  });

  if (!canWrite) {
    return (
      <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
        You need an owner or admin role to author requirements.
      </div>
    );
  }

  const inputCls =
    "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600";

  return (
    <Card className="max-w-xl space-y-3 p-4">
      <Field label="Title">
        <input
          className={inputCls}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Quarterly best-execution review"
        />
      </Field>
      <Field label="Description">
        <textarea
          className={inputCls}
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select
            className={inputCls}
            value={category}
            onChange={(e) => setCategory(e.target.value as ComplianceCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Authority">
          <select
            className={inputCls}
            value={authority}
            onChange={(e) => setAuthority(e.target.value as ComplianceAuthority)}
          >
            {AUTHORITIES.map((a) => (
              <option key={a} value={a}>
                {AUTHORITY_LABEL[a]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Frequency">
          <select
            className={inputCls}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as ComplianceFrequency)}
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {FREQUENCY_LABEL[f]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Lead-time (days)">
          <input
            type="number"
            min={0}
            className={inputCls}
            value={lead}
            onChange={(e) => setLead(Math.max(0, Number(e.target.value)))}
          />
        </Field>
      </div>
      {create.error && <p className="text-xs text-red-300">{create.error.message}</p>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!title.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Spinner className="size-4" /> : <Plus className="size-4" />}
          Create requirement
        </Button>
      </div>
    </Card>
  );
}

export function RequirementsPage() {
  return (
    <TenantLayout>
      <RequirementsInner />
    </TenantLayout>
  );
}
